import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import type {
  CapacityDrift,
  CropAlert,
  CropAlertFilters,
  CropAlertPage,
  CropAlertSummary,
  Plan,
  PlanAuditEntry,
  PlanBody,
  PlanDetail,
  PlanEditBody,
  PlanOutcome,
  PlanPreview,
  PlanningRackRow,
  PlanningRoomCapacity,
  RoomCapacityBody,
  RoomDay,
  StageActualOutcome,
  ProductionTask,
  ProductionTaskBoard,
  ProductionTaskBody,
  ProductionTaskQuery,
  ProductionTaskThread,
  TimelineEntry,
  YieldSpec,
  YieldSpecBody,
} from './production-planning.types';

const BASE = '/users/v1/admin/planning';
const KEY = ['production-planning'] as const;

/**
 * Anything that changes a plan can change ANY room's picture — a placement takes
 * racks a second plan was about to be offered, cancelling gives them back, and
 * scheduling mints work on the board. So mutations invalidate the whole planning
 * key rather than guessing which slices moved; being clever here is how a
 * timeline ends up drawing a plan that is no longer there.
 */
function usePlanningMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) });
}

/** A refresh somebody asked for on a screen that shows many planning reads at once (the plan
 *  record: the plan, its history, its alerts, its tasks). Refetches every ACTIVE planning
 *  query — the same whole-key reach a mutation has (see above), so no tab can be left on
 *  the old picture. Resolves when they have all landed. */
export function useRefreshPlanning() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

// ── configuration ─────────────────────────────────────────────────────────────

/**
 * The CONFIGURED planning geometry of every room, with the site's OBSERVED
 * numbers beside each.
 *
 * ⚠ Not `useRoomCapacity` — that name is already taken by the production
 * domain's read of `site_topology_rooms`, which is the OBSERVED physical fact
 * and carries `rack_count`. These are two different questions about one room and
 * the console shows both; sharing a name would guarantee somebody eventually
 * planned against the wrong one.
 */
export function usePlanningRoomCapacity(siteId: string) {
  return useQuery({
    queryKey: [...KEY, 'capacity', siteId],
    queryFn: () => api.get<PlanningRoomCapacity[]>(withQuery(`${BASE}/capacity`, { site_id: siteId })),
    enabled: Boolean(siteId),
  });
}

/**
 * Where the cloud's rack numbers and the site's have come apart (R-354).
 *
 * Deliberately a SEPARATE query rather than a field on the capacity list: it
 * joins the PLANS that book each room, which the card grid has no use for, and a
 * planning mutation must invalidate it — which it does, since the key sits under
 * `['production-planning']` like every other read here.
 *
 * ⚠ An empty `rows` is the healthy answer, not a missing one. The screen must
 * say nothing at all in that case — a banner that is always present is a banner
 * nobody reads.
 */
export function useCapacityDrift(siteId: string) {
  return useQuery({
    queryKey: [...KEY, 'capacity-drift', siteId],
    queryFn: () => api.get<CapacityDrift>(withQuery(`${BASE}/capacity/drift`, { site_id: siteId })),
    enabled: Boolean(siteId),
  });
}

export function useSaveRoomCapacity() {
  return usePlanningMutation((body: RoomCapacityBody) =>
    api.post<PlanningRoomCapacity>(`${BASE}/capacity`, body),
  );
}

export function useDeleteRoomCapacity() {
  return usePlanningMutation(({ siteId, roomCode }: { siteId: string; roomCode: string }) =>
    api.delete<boolean>(withQuery(`${BASE}/capacity`, { site_id: siteId, room_code: roomCode })),
  );
}

export function useYieldSpecs() {
  return useQuery({
    queryKey: [...KEY, 'yields'],
    queryFn: () => api.get<YieldSpec[]>(`${BASE}/yields`),
  });
}

export function useSaveYieldSpec() {
  return usePlanningMutation((body: YieldSpecBody) => api.post<string>(`${BASE}/yields`, body));
}

export function useDeleteYieldSpec() {
  return usePlanningMutation((id: string) => api.delete<boolean>(`${BASE}/yields/${id}`));
}

// ── planning ──────────────────────────────────────────────────────────────────

/**
 * The wizard's live answer. Runs the SAME code the save runs, so a preview whose
 * `fits` is true is a save that succeeds — which is the whole reason the Save
 * button reads that field instead of computing anything itself.
 *
 * Deliberately a QUERY, not a mutation: it writes nothing, and keying it on the
 * inputs means the wizard re-answers as they are typed without a manual refetch.
 */
export function usePlanPreview(body: PlanBody | null) {
  return useQuery({
    queryKey: [...KEY, 'preview', body],
    queryFn: () => api.post<PlanPreview>(`${BASE}/plans/preview`, body),
    enabled: Boolean(
      body?.site_id && body?.crop_id && body?.profile_id && body?.target_grams && body?.start_date,
    ),
  });
}

export function usePlans(siteId: string, status?: string) {
  return useQuery({
    queryKey: [...KEY, 'plans', siteId, status ?? 'all'],
    queryFn: () => api.get<Plan[]>(withQuery(`${BASE}/plans`, { site_id: siteId, status })),
    enabled: Boolean(siteId),
  });
}

export function usePlan(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'plan', id],
    queryFn: () => api.get<PlanDetail>(`${BASE}/plans/${id}`),
    enabled: Boolean(id),
  });
}

export function usePlanAudit(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'plan-audit', id],
    queryFn: () => api.get<PlanAuditEntry[]>(`${BASE}/plans/${id}/audit`),
    enabled: Boolean(id),
  });
}

export function useCreatePlan() {
  return usePlanningMutation((body: PlanBody) => api.post<PlanOutcome>(`${BASE}/plans`, body));
}

export function useUpdatePlan() {
  return usePlanningMutation(({ id, ...body }: PlanEditBody & { id: string }) =>
    api.put<PlanOutcome>(`${BASE}/plans/${id}`, body),
  );
}

/** Discard a DRAFT plan, with its stages, allocations, rack bookings and the
 *  tasks generated from it.
 *
 *  ⚠ **Draft only.** The backend refuses a `scheduled` plan (it is holding racks
 *  on a calendar other people are planning around), an `active` one (it has
 *  trays in a room) and all three terminal states (they are the record of a crop
 *  that ran — including *why* it was abandoned, which is the one thing a
 *  cancelled plan exists to preserve). The refusal names which, so show the
 *  server's sentence rather than pre-judging it here. The audit trail is
 *  deliberately kept.
 *
 *  Goes through `usePlanningMutation` like every other plan write, so the list,
 *  the timeline and the capacity rail all re-read — a deleted draft that lingers
 *  in a cached rack count is a rack nobody can book. */
export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/plans/${id}`),
    onSuccess: (_res, id) => {
      // ⚠ EVICT the deleted plan's own queries BEFORE invalidating the rest.
      // `usePlanningMutation` invalidates the whole planning key, and the record
      // page is still mounted for the instant between the delete and the
      // navigation — so a plain invalidate immediately REFETCHES the plan that
      // was just deleted and logs a 404 for it. Removing them first means there
      // is nothing left to refetch. (Backend R-358 also corrected that read's
      // status: a missing plan answered 409, which reads as "it exists but
      // refuses".)
      qc.removeQueries({ queryKey: [...KEY, 'plan', id] });
      qc.removeQueries({ queryKey: [...KEY, 'plan-audit', id] });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Move a plan through its lifecycle. Scheduling is the commit point — it
 * re-asserts the capacity invariant (the plan may have been drafted days ago and
 * lost its racks since) and mints the work.
 */
export function useTransitionPlan() {
  return usePlanningMutation(
    ({
      id,
      ...body
    }: {
      id: string;
      expected_version: number;
      status: string;
      reason?: string;
      /**
       * Complete a plan whose stages are not all recorded as finished. Send it
       * ONLY after the server has answered `needs_force` and the operator has
       * given a reason — it marks the unfinished stages `skipped`, which is a
       * record of the crop being cut short.
       */
      force?: boolean;
    }) => api.post<PlanOutcome>(`${BASE}/plans/${id}/transition`, body),
  );
}

export function useRecordStageActual() {
  return usePlanningMutation(
    ({ id, ...body }: { id: string; started: boolean; note?: string; at_ms?: number }) =>
      api.post<StageActualOutcome>(`${BASE}/stages/${id}/actual`, body),
  );
}

// ── the views ─────────────────────────────────────────────────────────────────

export function useTimeline(siteId: string, from: string, to: string) {
  return useQuery({
    queryKey: [...KEY, 'timeline', siteId, from, to],
    queryFn: () => api.get<TimelineEntry[]>(withQuery(`${BASE}/timeline`, { site_id: siteId, from, to })),
    enabled: Boolean(siteId && from && to),
  });
}

export function useRoomDays(siteId: string, from: string, to: string) {
  return useQuery({
    queryKey: [...KEY, 'room-days', siteId, from, to],
    queryFn: () => api.get<RoomDay[]>(withQuery(`${BASE}/rooms/days`, { site_id: siteId, from, to })),
    enabled: Boolean(siteId && from && to),
  });
}

/**
 * One room's rack rows. `until` makes it answer for a WINDOW rather than a day —
 * which is what the wizard's rack map needs, since a row taken for any part of a
 * phase is taken for that phase (R-324).
 */
export function useRackGrid(siteId: string, roomCode: string | null, on: string, until?: string) {
  return useQuery({
    queryKey: [...KEY, 'racks', siteId, roomCode, on, until ?? ''],
    queryFn: () =>
      api.get<PlanningRackRow[]>(
        withQuery(`${BASE}/rooms/racks`, {
          site_id: siteId,
          room_code: roomCode,
          on,
          ...(until ? { until } : {}),
        }),
      ),
    enabled: Boolean(siteId && roomCode && on),
  });
}

// ── the backlog ───────────────────────────────────────────────────────────────

export function useProductionTasks(q: ProductionTaskQuery) {
  return useQuery({
    queryKey: [...KEY, 'tasks', q],
    queryFn: () => api.get<ProductionTaskBoard>(withQuery(`${BASE}/tasks`, { ...q })),
    enabled: Boolean(q.site_id || q.plan_id),
  });
}

export function useTaskThread(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'task-thread', id],
    queryFn: () => api.get<ProductionTaskThread>(`${BASE}/tasks/${id}/thread`),
    enabled: Boolean(id),
  });
}

export function useSaveTask() {
  return usePlanningMutation((body: ProductionTaskBody) => api.post<ProductionTask>(`${BASE}/tasks`, body));
}

export function useTransitionTask() {
  return usePlanningMutation(({ id, ...body }: { id: string; status: string; blocked_reason?: string }) =>
    api.post<ProductionTask>(`${BASE}/tasks/${id}/transition`, body),
  );
}

export function useAssignTask() {
  return usePlanningMutation(({ id, assignee_id }: { id: string; assignee_id: string }) =>
    api.post<ProductionTask>(`${BASE}/tasks/${id}/assign`, { assignee_id }),
  );
}

export function useCommentOnTask() {
  return usePlanningMutation(({ id, body }: { id: string; body: string }) =>
    api.post<boolean>(`${BASE}/tasks/${id}/comments`, { body }),
  );
}

export function useDeleteTask() {
  return usePlanningMutation((id: string) => api.delete<boolean>(`${BASE}/tasks/${id}`));
}

// ── crop-cycle alerts (R-353) ─────────────────────────────────────────────────
// Site-wide, NOT per-recipient: the Alerts page answers "what has this farm's
// crops done", which is not the same question as "what was sent to me". The
// inbox keeps the second.

const ALERT_PAGE = 30;

/**
 * One page of crop alerts, newest first, keyset-paginated.
 *
 * ⚠ The rows come back in the SERVER's order and are rendered in it. Sorting them
 * client-side would reorder only the pages already fetched, so "most severe
 * first" would silently mean "most severe of the first 30".
 */
export function useCropAlerts(siteId: string, filters: CropAlertFilters) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'alerts', siteId, filters],
    queryFn: ({ pageParam }) => fetchAlertPage(siteId, filters, pageParam as string),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(siteId),
    staleTime: 10_000,
  });
}

/** One page of the alert list: `cursor` empty = the newest. Shared by the paged
 *  query, the head poll and the refresh below, so they can never disagree on the URL. */
function fetchAlertPage(siteId: string, filters: CropAlertFilters, cursor?: string) {
  return api.get<CropAlertPage>(
    withQuery(`${BASE}/alerts`, {
      site_id: siteId,
      plan_id: filters.plan_id,
      room_code: filters.room_code,
      severity: filters.severity,
      kind: filters.kind,
      // Omitted rather than sent as `false`: absent already means "everything".
      unacked: filters.unacked ? 'true' : undefined,
      limit: ALERT_PAGE,
      cursor,
    }),
  );
}

/** The alert list's NEWEST page, polled — what tells an open Alerts page that alerts
 *  were raised while it was on screen. A separate query on purpose: re-polling the
 *  paged query itself would refetch every page from its old cursors, and rows raised
 *  since would push older ones across a page boundary and lose them. */
export function useCropAlertsHead(siteId: string, filters: CropAlertFilters, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'alerts-head', siteId, filters],
    queryFn: () => fetchAlertPage(siteId, filters),
    refetchInterval: 30_000,
    enabled: Boolean(siteId) && enabled,
  });
}

/** Puts alerts the head poll found at the TOP of the paged list's first page — no
 *  refetch, no lost scroll position; the next-page cursor belongs to the OLDEST row,
 *  which has not changed. */
export function usePrependCropAlerts(siteId: string, filters: CropAlertFilters) {
  const qc = useQueryClient();
  return (rows: CropAlert[]) =>
    qc.setQueryData<InfiniteData<CropAlertPage>>([...KEY, 'alerts', siteId, filters], (d) => {
      const [first, ...rest] = d?.pages ?? [];
      if (!d || !first) return d;
      return { ...d, pages: [{ ...first, rows: [...rows, ...first.rows] }, ...rest] };
    });
}

/** A refresh somebody asked for: back to the NEWEST page, fresh. The newest page is
 *  fetched on its own and REPLACES the pages in one write (refetching a multi-page
 *  infinite query re-requests every page from stale cursors and drops rows), so the
 *  rows on screen stay until it lands and the paged query never shows a loader of
 *  its own. The header tiles refresh with it. */
export function useRefreshCropAlerts(siteId: string, filters: CropAlertFilters) {
  const qc = useQueryClient();
  return async () => {
    const [fresh] = await Promise.all([
      fetchAlertPage(siteId, filters),
      qc.refetchQueries({ queryKey: [...KEY, 'alerts-head', siteId] }),
      qc.refetchQueries({ queryKey: [...KEY, 'alerts-summary', siteId] }),
    ]);
    qc.setQueryData<InfiniteData<CropAlertPage>>([...KEY, 'alerts', siteId, filters], {
      pages: [fresh],
      pageParams: [''],
    });
  };
}

/**
 * The header tiles. Polled on the bell's cadence (30 s) because an operator
 * leaves this page open on a wall screen — a stale "0 critical" is exactly the
 * lie the frontend rules forbid.
 */
export function useCropAlertSummary(siteId: string, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'alerts-summary', siteId],
    queryFn: () => api.get<CropAlertSummary>(withQuery(`${BASE}/alerts/summary`, { site_id: siteId })),
    // `enabled` is for callers OUTSIDE the page — the sidebar badge rides every
    // page load, so it has to be able to stay silent when the reader lacks the
    // permission or no site is chosen yet. Defaulted true so the page's own call
    // is unchanged.
    enabled: Boolean(siteId) && enabled,
    refetchInterval: 30_000,
  });
}

/** The one number the sidebar badge shows: alerts nobody has acknowledged.
 *
 * All three severities, not just critical. The badge answers "is there anything
 * here I have not looked at", which is the same question the bell's unread count
 * answers — a warning nobody has seen is still unseen. */
export function unackedCropAlerts(s: CropAlertSummary | undefined): number {
  if (!s) return 0;
  return s.critical_unacked + s.warning_unacked + s.info_unacked;
}

/**
 * Acknowledge one alert.
 *
 * Invalidates the notifications key as well as planning's: an ack writes a
 * recipient row with `read_at`, so the bell's badge and the inbox row it came
 * from are both stale the moment this succeeds.
 */
export function useAckCropAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${BASE}/alerts/${id}/ack`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
